/**
 * Minimal structural shape of i18next's `t`; the real `TFunction` from
 * react-i18next is assignable to it.
 */
export type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

/** Where the error surfaced; lets an override swap in context-appropriate copy. */
export type TranslateContext = 'platform';

/**
 * App-specific rule that wins over the generic `apiErrors:codes.<code>`
 * convention. Needed only when a code needs a status filter, a call-site
 * `context` variant, or a custom interpolation of `details`.
 */
export interface ApiErrorOverride {
  /** Full `namespace:key` path into the locale bundles. */
  key: string;
  /** Pulls the interpolation values this code's copy needs out of `details`. */
  interpolate?: (details: unknown) => Record<string, unknown>;
  /** Alternative key per call-site context (falls back to `key`). */
  contextKeys?: Partial<Record<TranslateContext, string>>;
  /** Restricts the override to specific HTTP statuses. Omit when the code means the same at any status. */
  statuses?: number[];
}

export interface TranslateApiErrorOptions {
  context?: TranslateContext;
  overrides?: Partial<Record<string, ApiErrorOverride>>;
  /**
   * Call-site specific copy ("Could not load the plans") used when the error
   * is not an `ApiError` with a known code/status. Prefer passing a translated
   * string here over leaking `error.message`.
   */
  fallback?: string;
}

/**
 * Structural view of `ApiError` (packages/api-client). Duck-typed on purpose:
 * this package must not depend on api-client, and it keeps working when a
 * test replaces the api-client module with a partial mock.
 */
interface ApiErrorLike extends Error {
  code: string;
  status?: number;
  details?: unknown;
  /**
   * True when api-client derived `code` from the HTTP status because the body
   * carried none (e.g. a Nest guard's 401 / 409). Such a code says nothing about
   * the failure ("invalid_credentials" for a data-load 401), so the status wins.
   */
  codeIsSynthesized?: boolean;
}

function isApiError(error: unknown): error is ApiErrorLike {
  return error instanceof Error && typeof (error as { code?: unknown }).code === 'string';
}

/**
 * An `Error` whose `message` is ALREADY user-facing, localized copy (built with
 * `translateApiError` / `t(...)` by app code that has to re-throw, e.g. a form's
 * `onSubmit`). `translateApiError` shows its message as-is. Every other plain
 * `Error` (a network `TypeError`, a `ZodError`, a bug) resolves to the call-site
 * fallback instead — its text is developer output, not copy.
 */
export class LocalizedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'LocalizedError';
  }
}

const MISSING = '__jq4_missing_translation__';

/**
 * `t` that reports "no such key" instead of echoing the key back. Only the
 * `{{placeholders}}` the matched string actually contains are interpolated (from
 * `values`), so a stray body field named `count` / `context` / `lng` can never
 * alter how i18next resolves the key. A string whose placeholders the body did
 * not supply (older payload, hand-thrown DomainError) is rejected so a
 * half-rendered sentence never reaches the user.
 */
function lookup(t: TranslateFn, key: string, values?: Record<string, unknown>): string | undefined {
  const raw = t(key, { defaultValue: MISSING, skipInterpolation: true });
  if (raw === MISSING || raw === key) return undefined;

  const wanted = [...raw.matchAll(/{{\s*(\w+)\s*}}/g)].map((m) => m[1] as string);
  if (wanted.length === 0) return raw;

  const picked: Record<string, unknown> = {};
  for (const name of wanted) {
    const value = values?.[name];
    if (value === undefined) return undefined;
    picked[name] = value;
  }
  return t(key, picked);
}

/** Scalar fields of the backend error body (`ApiError.details`): `locationCapacity`, `field`, ... */
function scalarsFrom(details: unknown): Record<string, unknown> {
  if (typeof details !== 'object' || details === null) return {};
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(details)) {
    if (k === 'code' || k === 'message') continue;
    if (typeof v === 'string' || typeof v === 'number') out[k] = v;
  }
  return out;
}

function matchesStatus(override: ApiErrorOverride, status: number | undefined): boolean {
  return (
    override.statuses === undefined || (status !== undefined && override.statuses.includes(status))
  );
}

function statusKey(status: number | undefined): string | undefined {
  if (status === undefined) return undefined;
  return status >= 500 ? 'server' : String(status);
}

/**
 * translateApiError — turns anything thrown by the API layer into copy in the
 * active UI language. NEVER renders the backend's `message` (developer text in
 * English, occasionally Spanish).
 *
 * Resolution order for an `ApiError`:
 *  1. `options.overrides[code]` (status filter, `context`, custom interpolation)
 *  2. `apiErrors:codes.<code>__<status>`  (overloaded codes, e.g. `forbidden`)
 *  3. `apiErrors:codes.<code>`            (the convention — add a key, done)
 *  4. `apiErrors:status.<status>`         (Nest guards/pipes answer without a code)
 *  5. `options.fallback`
 *  6. `apiErrors:codes.unknown`
 *
 * A code that api-client synthesized from the HTTP status (the body had none)
 * is ignored: steps 2-3 are skipped and the status copy wins.
 *
 * Non-`ApiError` values: a `LocalizedError` shows its (already localized)
 * message; everything else — network `TypeError`, `ZodError`, any plain `Error` —
 * resolves to `fallback ?? codes.unknown`, never to its developer text.
 */
export function translateApiError(
  error: unknown,
  t: TranslateFn,
  options: TranslateApiErrorOptions = {},
): string {
  if (!isApiError(error)) {
    if (error instanceof LocalizedError && error.message) return error.message;
    return options.fallback ?? t('apiErrors:codes.unknown');
  }

  const override = options.overrides?.[error.code];
  if (override && matchesStatus(override, error.status)) {
    const key = (options.context && override.contextKeys?.[options.context]) || override.key;
    const overrideValues = override.interpolate ? override.interpolate(error.details) : {};
    // A mapped code whose expected fields are missing/malformed in `details`
    // must not render a sentence with a literal "undefined" spliced in; drop
    // to the generic code copy below instead.
    if (Object.values(overrideValues).every((value) => value !== undefined)) {
      return t(key, overrideValues);
    }
  }

  return translateErrorCode(error.codeIsSynthesized ? 'unknown' : error.code, t, {
    status: error.status,
    details: error.details,
    fallback: options.fallback,
  });
}

export interface TranslateErrorCodeOptions {
  status?: number;
  /** Raw error body (or `{ field }`); only the placeholders the copy contains are read from it. */
  details?: unknown;
  fallback?: string;
}

/**
 * Same resolution as `translateApiError` for callers that only hold a code —
 * e.g. the per-item errors of a batch response (`{ code, message }`) or a
 * persisted offline-queue rejection — and never an `ApiError` instance.
 */
export function translateErrorCode(
  code: string,
  t: TranslateFn,
  options: TranslateErrorCodeOptions = {},
): string {
  const values = scalarsFrom(options.details);
  // `field` is a backend field id (`parking_zone`): show its translated name.
  if (typeof values.field === 'string') {
    values.field = lookup(t, `apiErrors:fields.${values.field}`) ?? values.field;
  }

  if (options.status !== undefined) {
    const specific = lookup(t, `apiErrors:codes.${code}__${options.status}`, values);
    if (specific) return specific;
  }

  if (code !== 'unknown') {
    // Without the body fields (batch / offline rejections carry only a code) a
    // string such as "Missing field: {{field}}" cannot render; `<code>__generic`
    // is its placeholder-free wording.
    const generic =
      lookup(t, `apiErrors:codes.${code}`, values) ?? lookup(t, `apiErrors:codes.${code}__generic`);
    if (generic) return generic;
  }

  const status = statusKey(options.status);
  if (status) {
    const byStatus = lookup(t, `apiErrors:status.${status}`);
    if (byStatus) return byStatus;
  }

  return options.fallback ?? t('apiErrors:codes.unknown');
}
